import { AppConfig } from '../config/AppConfig';
import { StrokeSyncService } from './StrokeSyncService';
import { useDeviceStore } from '../store/useDeviceStore';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { IActivity } from '@eduwrite/shared';

export interface AvailableSubjectsCacheEntry {
    subjects: string[];
    timestamp: number;
}

export interface FetchActivitiesParams {
    yearGroup: string;
    subject: string;
    strand?: string;
    count?: number;
    customServerUrl?: string;
}

export interface FreestyleLogParams {
    deviceId: string;
    keyStage?: string;
    yearGroup: string;
    subject: string;
    strand?: string;
    activityTitle: string;
    activityType?: string;
    score?: number | null;
    isCorrect?: boolean | null;
    durationMs?: number | null;
    customServerUrl?: string;
}

const CACHE_TTL_MS = 60 * 1000; // 60 seconds

export class FreestyleService {
    private static cache: Record<string, AvailableSubjectsCacheEntry> = {};
    private static activeServerUrl: string | null = null;

    /**
     * Clear cached subjects for a specific year group or entire cache.
     */
    static invalidateCache(yearGroup?: string) {
        if (yearGroup) {
            delete this.cache[yearGroup.toLowerCase()];
        } else {
            this.cache = {};
        }
    }

    /**
     * Check if a valid (unexpired) cache entry exists for the given year group.
     */
    static getCachedSubjects(yearGroup: string): string[] | null {
        const key = yearGroup.toLowerCase();
        const entry = this.cache[key];
        if (entry && Date.now() - entry.timestamp < CACHE_TTL_MS) {
            return entry.subjects;
        }
        return null;
    }

    /**
     * Resolves an ordered list of candidate sync-server base URLs.
     */
    static getCandidateUrls(customServerUrl?: string | null): string[] {
        const registeredServer = customServerUrl || useDeviceStore.getState().serverUrl;
        const syncServerUrl = StrokeSyncService.getServerUrl();
        const configuredUrl = process.env.VITE_SYNC_SERVER_URL;

        const candidates: (string | undefined | null)[] = [
            this.activeServerUrl,
            registeredServer,
            syncServerUrl,
            configuredUrl,
            AppConfig.DEMO_SERVER_URL,
            ...AppConfig.DNS_FALLBACK_HOSTS,
            AppConfig.DEFAULT_SERVER_URL,
        ];

        const seen = new Set<string>();
        const clean: string[] = [];

        for (const url of candidates) {
            if (!url || typeof url !== 'string') continue;
            const normalized = url.trim().replace(/\/+$/, '');
            if (normalized && !seen.has(normalized)) {
                seen.add(normalized);
                clean.push(normalized);
            }
        }

        return clean;
    }

    /**
     * Fetches available subjects for a year group across candidate sync-servers.
     * Retries and fails over across hosts. Does not poison cache on complete network failure.
     */
    static async fetchAvailableSubjects(
        yearGroup: string,
        options: { forceRefresh?: boolean; customServerUrl?: string } = {}
    ): Promise<{ subjects: string[]; fromCache: boolean; isOffline: boolean }> {
        const key = yearGroup.toLowerCase().trim();

        if (!options.forceRefresh) {
            const cached = this.getCachedSubjects(key);
            if (cached) {
                return { subjects: cached, fromCache: true, isOffline: false };
            }
        }

        const candidates = this.getCandidateUrls(options.customServerUrl);
        let lastError: unknown = null;

        for (const baseUrl of candidates) {
            try {
                const url = `${baseUrl}/api/freestyle/available-subjects?yearGroup=${encodeURIComponent(key)}`;
                const res = await fetchWithRetry(url, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    timeoutMs: 3500,
                    maxRetries: 0,
                    retryOnNetworkError: false,
                });

                if (res.ok) {
                    const data = await res.json();
                    if (Array.isArray(data.availableSubjects) && data.availableSubjects.length > 0) {
                        const subjects: string[] = data.availableSubjects;
                        this.cache[key] = {
                            subjects,
                            timestamp: Date.now(),
                        };
                        this.activeServerUrl = baseUrl;
                        StrokeSyncService.setServerUrl(baseUrl);
                        return { subjects, fromCache: false, isOffline: false };
                    }
                }
            } catch (err) {
                lastError = err;
            }
        }

        console.warn(`[FreestyleService] All hosts failed to resolve subjects for ${yearGroup}:`, lastError);

        // Do NOT store failure permanently into cache.
        // Return ['maths'] as safe local fallback but indicate offline status.
        return {
            subjects: ['maths'],
            fromCache: false,
            isOffline: true,
        };
    }

    /**
     * Fetches curriculum activities for free learning practice across candidate hosts.
     */
    static async fetchActivities(params: FetchActivitiesParams): Promise<IActivity[]> {
        const { yearGroup, subject, strand, count = 5, customServerUrl } = params;
        const candidates = this.getCandidateUrls(customServerUrl);
        const strandParam = strand ? `&strand=${encodeURIComponent(strand)}` : '';

        for (const baseUrl of candidates) {
            try {
                const url = `${baseUrl}/api/freestyle/activities?yearGroup=${encodeURIComponent(yearGroup)}&subject=${encodeURIComponent(subject)}${strandParam}&count=${count}`;
                const res = await fetchWithRetry(url, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    timeoutMs: 4000,
                    maxRetries: 1,
                });

                if (res.ok) {
                    const data: IActivity[] = await res.json();
                    if (Array.isArray(data)) {
                        this.activeServerUrl = baseUrl;
                        StrokeSyncService.setServerUrl(baseUrl);
                        return data;
                    }
                }
            } catch (err) {
                console.warn(`[FreestyleService] ${baseUrl} failed to fetch activities:`, err);
            }
        }

        return [];
    }

    /**
     * Logs non-PII freestyle completion across working hosts.
     */
    static async logCompletion(params: FreestyleLogParams): Promise<void> {
        const { deviceId, customServerUrl, ...payload } = params;
        if (!deviceId) return;

        const candidates = this.getCandidateUrls(customServerUrl);
        for (const baseUrl of candidates) {
            try {
                const res = await fetch(`${baseUrl}/api/freestyle/log`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ deviceId, ...payload }),
                });
                if (res.ok) {
                    return;
                }
            } catch {
                // Non-blocking log, fail silently to next host
            }
        }
    }
}
